import type { ApiClient } from '@carbonwebmail/api-client'
import type { OrganizationSettingsDto } from '@carbonwebmail/contracts'
import { useCallback, useEffect, useState } from 'react'
import type { AsyncState } from '../../ui/Async.js'
import { useLoaded } from '../../ui/useLoaded.js'

/**
 * An organisation's own settings.
 *
 * The whole record is sent on save rather than a patch of what changed. A form
 * shows all six fields and somebody edits a couple; a patch would make "I
 * cleared the reply address" and "I did not touch the reply address" the same
 * request, and the first is a thing people deliberately do.
 */

export type SettingsState = AsyncState<OrganizationSettingsDto>

export function useSettings({ client }: { readonly client: ApiClient }) {
  const load = useCallback(() => client.settings(), [client])
  const { state } = useLoaded<OrganizationSettingsDto>(
    load,
    'We could not load your settings. Please try again in a moment.',
  )

  const [draft, setDraft] = useState<OrganizationSettingsDto | undefined>(undefined)
  const [saved, setSaved] = useState(false)
  const [problem, setProblem] = useState<string | undefined>(undefined)
  const [saving, setSaving] = useState(false)

  /**
   * Filled once, when the settings arrive.
   *
   * Guarded on the draft rather than on the loaded value, so nothing can
   * overwrite what somebody has typed since.
   */
  useEffect(() => {
    if (state.status !== 'ready' || draft !== undefined) return
    setDraft(state.value)
  }, [state, draft])

  const change = useCallback(
    <K extends keyof OrganizationSettingsDto>(key: K, value: OrganizationSettingsDto[K]) => {
      setDraft((current) => (current === undefined ? current : { ...current, [key]: value }))
      setSaved(false)
      setProblem(undefined)
    },
    [],
  )

  const save = useCallback(async () => {
    if (draft === undefined) return
    setSaving(true)
    setProblem(undefined)
    try {
      await client.saveSettings(draft)
      setSaved(true)
    } catch {
      // The contract rejects a malformed address on both sides, so the common
      // failure here is one of those rather than a broken request.
      setProblem('We could not save that. Check the email addresses and try again.')
    } finally {
      setSaving(false)
    }
  }, [client, draft])

  return { state, draft, saved, problem, saving, change, save }
}
